import { useQuery } from '@tanstack/react-query';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import config from '../config';
import { getAssets } from '../utils/getAssets';
import { useAlgo } from './useAlgo';
import { useUserAssetsStore } from './useUserAssetsStore';

const DEFAULT_TOKEN_CHOICES = config.flipTokenChoices;

const useFlipsWalletQuery = () => {
  return useQuery({
    queryKey: ['flipsWalletAssets'],
    queryFn: async () => {
      const flipAssets = await getAssets(process.env.NEXT_PUBLIC_FLIP_WALLET);
      const availableFlipAssets = flipAssets.filter((asset) => asset.amount > 0);
      const choices = DEFAULT_TOKEN_CHOICES.filter((choice) => availableFlipAssets.find((asset) => asset['asset-id'] === choice.id));
      return choices;
    },
  });
};

const FlipsContext = createContext({});
export const useFlips = () => useContext(FlipsContext);

export const FlipsProvider = ({ children }) => {
  const { address } = useAlgo();
  const [betAmount, setBetAmount] = useState();
  const [choiceOfToken, setChoiceOfToken] = useState(parseInt(process.env.NEXT_PUBLIC_TREAT_TOKEN_ID));
  const [choiceToWin, setChoiceToWin] = useState('heads');
  const [tokenTotals, setTokenTotals] = useState({ user: 0, house: 0 });
  const [maxBet, setMaxBet] = useState(0);
  
  const { userAssets } = useUserAssetsStore();

  const flipsWalletQuery = useFlipsWalletQuery();
  const tokenChoices = flipsWalletQuery.data || [];

  const getChoiceOfTokenName =(assetId) => {
    const choice = tokenChoices.find((choice) => choice.id === assetId);
    return choice?.name;
  }

  const getTokenTotals = (token) => {
    const tokenTotal = tokenChoices.find((asset) => asset['asset-id'] === token);
    return tokenTotal?.amount || 0;
  };

  const getUserTotalTokens = (token) => {
    const tokenTotal = userAssets.find((asset) => asset['asset-id'] === token);
    return tokenTotal?.amount || 0;
  };

  const fetchNewTotals = () => {
    let totals = {
      user: 0,
      house: 0,
    };
    const divisible = tokenChoices.find((choice) => choice.id === choiceOfToken)?.divisible;
    totals.user = getUserTotalTokens(choiceOfToken);
    totals.house = getTokenTotals(choiceOfToken);
    if (divisible) {
      totals.user = totals.user / divisible;
      totals.house = totals.house / divisible;
    }
    setTokenTotals(totals);
    return totals;
  };

  useEffect(() => {
    if (!address) return;

    fetchNewTotals();

    const interval = setInterval(() => {
      if (address) {
        fetchNewTotals();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [choiceOfToken, address]);

  useEffect(() => {
    const maxHouseBet = Math.ceil(tokenTotals.house / 50);
    const maxBet = Math.min(maxHouseBet, tokenTotals.user);
    setMaxBet(tokenTotals?.user <= maxBet ? tokenTotals?.user : maxBet);
  }, [tokenTotals.house, tokenTotals.user]);

  return (
    <FlipsContext.Provider
      value={{
        betAmount,
        setBetAmount,
        choiceOfToken,
        getChoiceOfTokenName,
        setChoiceOfToken,
        choiceToWin,
        setChoiceToWin,
        tokenChoices,
        maxBet,
        tokenTotals,
        fetchNewTotals,
      }}
    >
      {children}
    </FlipsContext.Provider>
  );
};
